import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
import "./D3HelioChart.css";

// Base dimensions for scaling calculations
const BASE_SIZE = 800;

/**
 * Average semi-major axis (AU) for planets as fallback when distanceAu is not available
 * These are approximate average orbital distances from the Sun
 */
const PLANET_AVERAGE_DISTANCES = {
  mercury: 0.39,
  venus: 0.72,
  earth: 1.0,
  mars: 1.52,
  jupiter: 5.20,
  saturn: 9.58,
  uranus: 19.22,
  neptune: 30.07,
  pluto: 39.48,
  // Dwarf planets and asteroids (approximate)
  ceres: 2.77,
  pallas: 2.77,
  juno: 2.67,
  vesta: 2.36,
  chiron: 13.7,
  pholus: 20.3,
  makemake: 45.8,
  eris: 67.8,
  quaoar: 43.4,
  sedna: 506.0,
  haumea: 43.1,
  ixion: 39.6,
  gonggong: 67.3,
  orcus: 39.2,
};

/**
 * Planet colors for visualization
 */
const PLANET_COLORS = {
  mercury: "#8C7853",
  venus: "#FFC649",
  earth: "#6B93D6",
  mars: "#C1440E",
  jupiter: "#D8CA9D",
  saturn: "#FAD5A5",
  uranus: "#4FD0E7",
  neptune: "#4B70DD",
  pluto: "#C9C9C9",
  // Default color for other bodies
  default: "#FFFFFF",
};

// Format planet names to Title Case (camel-style display) for consistency
const formatPlanetName = (name = "") =>
  name
    .toString()
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

/**
 * Convert heliocentric polar coordinates to Cartesian
 * @param {number} distance - Distance from Sun (scaled pixels)
 * @param {number} angleDeg - Angle in degrees (0° = upward, increases counter-clockwise)
 * @param {number} centerX - X coordinate of center (Sun)
 * @param {number} centerY - Y coordinate of center (Sun)
 * @returns {Object} {x, y} Cartesian coordinates
 */
function polarToCartesian(distance, angleDeg, centerX, centerY) {
  // Convert to radians and adjust so 0° points upward
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  
  return {
    x: centerX + distance * Math.cos(angleRad),
    y: centerY + distance * Math.sin(angleRad),
  };
}

/**
 * Get planet order index for ratio-based positioning
 * This ensures planets are evenly spaced and don't overlap
 */
const PLANET_ORDER = {
  mercury: 1,
  venus: 2,
  earth: 3,
  mars: 4,
  jupiter: 5,
  saturn: 6,
  uranus: 7,
  neptune: 8,
  pluto: 9,
  // Dwarf planets and asteroids continue the sequence
  ceres: 10,
  pallas: 11,
  juno: 12,
  vesta: 13,
  chiron: 14,
  pholus: 15,
  makemake: 16,
  eris: 17,
  quaoar: 18,
  sedna: 19,
  haumea: 20,
  ixion: 21,
  gonggong: 22,
  orcus: 23,
};

/**
 * Get planet position index for ratio-based distance
 * Returns a ratio value (0-1) that determines distance from Sun
 */
function getPlanetPositionRatio(planet, totalPlanets) {
  const name = planet.name?.toLowerCase() || "";
  const order = PLANET_ORDER[name] || 999; // Unknown planets go to outer edge
  
  // Use ratio-based positioning: inner planets closer, outer planets further
  // But ensure minimum spacing to prevent overlap
  // Map order to ratio: 0.15 (closest) to 0.95 (furthest)
  const minRatio = 0.15; // Minimum distance from Sun (prevents overlap with Sun)
  const maxRatio = 0.95; // Maximum distance (leaves room for labels)
  
  // Use a smooth distribution - logarithmic-like but ensuring all planets are visible
  // For known planets, use their order; for unknown, distribute evenly
  if (order < 999) {
    // Known planets: use their order for consistent positioning
    const normalizedOrder = (order - 1) / Math.max(totalPlanets - 1, 1);
    return minRatio + normalizedOrder * (maxRatio - minRatio);
  } else {
    // Unknown planets: distribute in remaining space
    return maxRatio;
  }
}

/**
 * Format tooltip text for planet
 */
function formatPlanetTooltip(planet, distanceAu, angleDeg) {
  const name = formatPlanetName(planet.label || planet.name || "Unknown");
  const distance = distanceAu > 0 ? distanceAu.toFixed(3) : "N/A";
  const angle = angleDeg.toFixed(2);
  return `${name}\nHeliocentric Longitude: ${angle}°${distanceAu > 0 ? `\nDistance: ${distance} AU` : ""}`;
}

const D3HelioChart = ({ planets = [], aspects = [], width = 800, height = 800 }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const zoomGroupRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: BASE_SIZE, height: BASE_SIZE });

  // Calculate responsive chart size based on container
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Account for padding (30px on each side = 60px total, less on mobile)
      const padding = window.innerWidth <= 480 ? 20 : window.innerWidth <= 768 ? 30 : 60;
      const availableSize = Math.min(
        containerRect.width - padding,
        containerRect.height - padding,
        width,
        height
      );
      
      // Ensure minimum size but don't exceed base size
      const size = Math.max(Math.min(availableSize, BASE_SIZE), 300);
      setChartSize({ width: size, height: size });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    
    // Use ResizeObserver for more accurate container size detection
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [width, height]);

  useEffect(() => {
    if (!svgRef.current || !planets || planets.length === 0 || !chartSize.width || !chartSize.height) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current);
    
    // Use viewBox for responsive scaling - ensures chart always fits container
    // All coordinates use BASE_SIZE (800x800) - SVG viewBox handles scaling automatically
    svg
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${BASE_SIZE} ${BASE_SIZE}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Calculate center (Sun position - fixed at exact center)
    const centerX = BASE_SIZE / 2;
    const centerY = BASE_SIZE / 2;

    // Use ratio-based distance scaling for visibility
    // All planets will be evenly spaced in a circle, ensuring no overlap
    // Use fixed base values - viewBox will handle scaling
    const margin = 80; // Base margin
    const maxRadius = BASE_SIZE / 2 - margin; // Leave room for labels
    const minRadius = 30; // Minimum distance from Sun (prevents overlap with Sun circle)
    
    // Scale function: ratio (0-1) -> pixel radius (in viewBox coordinates)
    const radiusScale = (ratio) => minRadius + ratio * (maxRadius - minRadius);

    // Filter out Sun if present (Sun is the center, not a planet)
    const chartPlanets = planets.filter(
      (p) => p.name?.toLowerCase() !== "sun"
    );

    // Calculate position ratios for all planets
    const planetsWithRatios = chartPlanets.map((planet) => ({
      ...planet,
      positionRatio: getPlanetPositionRatio(planet, chartPlanets.length),
    }));

    // Create zoomable group - all chart elements go inside this
    const zoomGroup = svg.append("g").attr("class", "zoom-group");
    zoomGroupRef.current = zoomGroup;

    // Create main group for chart elements (inside zoom group)
    const chartGroup = zoomGroup.append("g").attr("class", "helio-chart-group");

    // Draw orbital paths (optional but recommended for clarity)
    chartGroup
      .selectAll(".orbit")
      .data(planetsWithRatios)
      .enter()
      .append("circle")
      .attr("class", "orbit")
      .attr("cx", centerX)
      .attr("cy", centerY)
      .attr("r", (d) => radiusScale(d.positionRatio))
      .attr("fill", "none")
      .attr("stroke", "#555")
      .attr("stroke-width", 1) // Base stroke width, viewBox scales it
      .attr("stroke-dasharray", "3,3") // Dash array scales with viewBox
      .attr("opacity", 0.4);

    // Draw degree guide (outer ring with degree markers)
    const degreeGuideGroup = chartGroup.append("g").attr("class", "degree-guide-group");
    const guideRadius = maxRadius + 30; // Outer radius for degree guide
    
    // Draw degree markers every 10 degrees
    for (let deg = 0; deg < 360; deg += 10) {
      const pos = polarToCartesian(guideRadius, deg, centerX, centerY);
      const innerPos = polarToCartesian(guideRadius - 8, deg, centerX, centerY);
      
      // Draw tick mark
      degreeGuideGroup
        .append("line")
        .attr("x1", innerPos.x)
        .attr("y1", innerPos.y)
        .attr("x2", pos.x)
        .attr("y2", pos.y)
        .attr("stroke", deg % 30 === 0 ? "#94A3B8" : "#64748B")
        .attr("stroke-width", deg % 30 === 0 ? 1.5 : 1)
        .attr("opacity", deg % 30 === 0 ? 0.8 : 0.5);
      
      // Add degree labels every 30 degrees (major divisions)
      if (deg % 30 === 0) {
        const labelPos = polarToCartesian(guideRadius + 15, deg, centerX, centerY);
        degreeGuideGroup
          .append("text")
          .attr("x", labelPos.x)
          .attr("y", labelPos.y)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("fill", "#94A3B8")
          .attr("font-size", "10")
          .attr("font-weight", "600")
          .attr("paint-order", "stroke")
          .attr("stroke", "#0B0B0B")
          .attr("stroke-width", "0.3")
          .text(`${deg}°`);
      }
    }

    // Draw aspect lines (show ALL aspects - astrologically accurate, not astronomically)
    // Draw aspects BEFORE planets so planets appear on top
    if (aspects && aspects.length > 0) {
      const aspectGroup = chartGroup.append("g").attr("class", "aspects-group");

      aspects.forEach((aspect) => {
        if (!aspect.planets || aspect.planets.length < 2) return;

        const [planet1Name, planet2Name] = aspect.planets;
        const planet1 = planetsWithRatios.find(
          (p) => p.name?.toLowerCase() === planet1Name?.toLowerCase()
        );
        const planet2 = planetsWithRatios.find(
          (p) => p.name?.toLowerCase() === planet2Name?.toLowerCase()
        );

        if (!planet1 || !planet2) return;

        const dist1 = radiusScale(planet1.positionRatio);
        const dist2 = radiusScale(planet2.positionRatio);
        const angle1 = planet1.longitude || 0;
        const angle2 = planet2.longitude || 0;

        const pos1 = polarToCartesian(dist1, angle1, centerX, centerY);
        const pos2 = polarToCartesian(dist2, angle2, centerX, centerY);

        // Determine aspect line color and style based on aspect type
        const aspectStyles = {
          conjunction: { color: "#6366F1", width: 2.5, opacity: 0.8 },
          opposition: { color: "#EF4444", width: 2.5, opacity: 0.8 },
          square: { color: "#F59E0B", width: 2, opacity: 0.75 },
          trine: { color: "#10B981", width: 2, opacity: 0.75 },
          sextile: { color: "#3B82F6", width: 1.5, opacity: 0.7 },
          semisextile: { color: "#8B5CF6", width: 1, opacity: 0.6 },
          semisquare: { color: "#EC4899", width: 1, opacity: 0.6 },
          sesquiquadrate: { color: "#F97316", width: 1, opacity: 0.6 },
          quincunx: { color: "#14B8A6", width: 1, opacity: 0.6 },
          quintile: { color: "#A855F7", width: 1, opacity: 0.5 },
          biquintile: { color: "#A855F7", width: 1, opacity: 0.5 },
        };
        
        const aspectType = aspect.aspect?.toLowerCase() || "";
        const style = aspectStyles[aspectType] || { 
          color: "#94A3B8", 
          width: 1, 
          opacity: 0.5 
        };
        const isMinor = aspect.type === "minor";

        aspectGroup
          .append("line")
          .attr("class", `aspect-line aspect-${aspectType}`)
          .attr("x1", pos1.x)
          .attr("y1", pos1.y)
          .attr("x2", pos2.x)
          .attr("y2", pos2.y)
          .attr("stroke", style.color)
          .attr("stroke-width", isMinor ? style.width * 0.7 : style.width) // Base widths, viewBox scales them
          .attr("stroke-dasharray", isMinor ? "4,4" : "none")
          .attr("opacity", isMinor ? style.opacity * 0.7 : style.opacity)
          .attr("title", `${planet1Name} ${aspect.aspect} ${planet2Name}${aspect.orb ? ` (${aspect.orb.toFixed(2)}° orb)` : ""}`)
          .style("cursor", "pointer");
      });
    }

    // Draw planets (after aspects so they appear on top)
    const planetGroup = chartGroup.append("g").attr("class", "planets-group");

    const planetCircles = planetGroup
      .selectAll(".planet")
      .data(planetsWithRatios)
      .enter()
      .append("circle")
      .attr("class", (d) => `planet planet-${d.name?.toLowerCase()}`)
      .attr("r", (d) => {
        // Larger planets get slightly bigger circles (base sizes, viewBox scales them)
        const name = d.name?.toLowerCase() || "";
        const largePlanets = ["jupiter", "saturn", "uranus", "neptune"];
        return largePlanets.includes(name) ? 7 : 6;
      })
      .attr("fill", (d) => {
        const name = d.name?.toLowerCase() || "";
        return PLANET_COLORS[name] || PLANET_COLORS.default;
      })
      .attr("stroke", "#FFF")
      .attr("stroke-width", 1.5) // Base stroke width, viewBox scales it
      .attr("cx", (d) => {
        const dist = radiusScale(d.positionRatio);
        const angle = d.longitude || 0;
        return polarToCartesian(dist, angle, centerX, centerY).x;
      })
      .attr("cy", (d) => {
        const dist = radiusScale(d.positionRatio);
        const angle = d.longitude || 0;
        return polarToCartesian(dist, angle, centerX, centerY).y;
      })
      .attr("title", (d) => {
        const actualDist = d.distanceAu || PLANET_AVERAGE_DISTANCES[d.name?.toLowerCase()] || 0;
        const angle = d.longitude || 0;
        return formatPlanetTooltip(d, actualDist, angle);
      })
      .style("cursor", "pointer")
      .on("mouseover", function() {
        d3.select(this).attr("r", (d) => {
          const name = d.name?.toLowerCase() || "";
          const largePlanets = ["jupiter", "saturn", "uranus", "neptune"];
          return (largePlanets.includes(name) ? 7 : 6) + 2; // Hover size
        });
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", (d) => {
          const name = d.name?.toLowerCase() || "";
          const largePlanets = ["jupiter", "saturn", "uranus", "neptune"];
          return largePlanets.includes(name) ? 7 : 6; // Normal size
        });
      });

    // Draw planet labels with planet colors
    const labelGroup = chartGroup.append("g").attr("class", "labels-group");

    labelGroup
      .selectAll(".planet-label")
      .data(planetsWithRatios)
      .enter()
      .append("text")
      .attr("class", (d) => `planet-label label-${d.name?.toLowerCase()}`)
      .attr("x", (d) => {
        const dist = radiusScale(d.positionRatio);
        const angle = d.longitude || 0;
        // Offset label outward from planet
        const labelDist = dist + 20;
        return polarToCartesian(labelDist, angle, centerX, centerY).x;
      })
      .attr("y", (d) => {
        const dist = radiusScale(d.positionRatio);
        const angle = d.longitude || 0;
        const labelDist = dist + 20;
        return polarToCartesian(labelDist, angle, centerX, centerY).y;
      })
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", (d) => {
        const name = d.name?.toLowerCase() || "";
        return PLANET_COLORS[name] || PLANET_COLORS.default;
      })
      .attr("font-size", "12") // No px - scales with viewBox
      .attr("font-weight", "600")
      .attr("paint-order", "stroke")
      .attr("stroke", "#0B0B0B")
      .attr("stroke-width", "0.8") // No px - scales with viewBox
      .attr("stroke-linejoin", "round")
      .text((d) => formatPlanetName(d.label || d.name || "Unknown"))
      .attr("title", (d) => {
        const actualDist = d.distanceAu || PLANET_AVERAGE_DISTANCES[d.name?.toLowerCase()] || 0;
        const angle = d.longitude || 0;
        return formatPlanetTooltip(d, actualDist, angle);
      })
      .style("cursor", "pointer")
      .style("pointer-events", "none"); // Labels don't intercept mouse events

    // Draw the Sun (fixed at center) - draw last so it's on top
    chartGroup
      .append("circle")
      .attr("class", "sun")
      .attr("cx", centerX)
      .attr("cy", centerY)
      .attr("r", 16)
      .attr("fill", "url(#sunGradient)")
      .attr("stroke", "#FFD700")
      .attr("stroke-width", 2.5)
      .attr("title", "Sun (center of heliocentric system)")
      .style("filter", "drop-shadow(0 0 8px rgba(255, 215, 0, 0.8))");

    // Create gradient for Sun
    const defs = svg.append("defs");
    const sunGradient = defs.append("radialGradient").attr("id", "sunGradient");
    sunGradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#FFD700");
    sunGradient
      .append("stop")
      .attr("offset", "70%")
      .attr("stop-color", "#FFA500");
    sunGradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#FF8C00");

    // Add reference direction indicator (0° points upward)
    const referenceGroup = chartGroup.append("g").attr("class", "reference-group");
    
    // Define arrowhead marker (if not already defined)
    if (svg.select("#arrowhead").empty()) {
      defs
        .append("marker")
        .attr("id", "arrowhead")
        .attr("markerWidth", 8)
        .attr("markerHeight", 8)
        .attr("refX", 4)
        .attr("refY", 4)
        .attr("orient", "auto")
        .append("polygon")
        .attr("points", "0,0 8,4 0,8")
        .attr("fill", "#94A3B8");
    }
    
    // Draw a small arrow pointing upward (0° reference direction)
    const refRadius = 25;
    
    referenceGroup
      .append("line")
      .attr("x1", centerX)
      .attr("y1", centerY - refRadius - 8)
      .attr("x2", centerX)
      .attr("y2", centerY - refRadius - 20)
      .attr("stroke", "#94A3B8")
      .attr("stroke-width", 1.5) // Base stroke width, viewBox scales it
      .attr("marker-end", "url(#arrowhead)")
      .attr("opacity", 0.7);

    referenceGroup
      .append("text")
      .attr("x", centerX)
      .attr("y", centerY - refRadius - 25)
      .attr("text-anchor", "middle")
      .attr("fill", "#94A3B8")
      .attr("font-size", "10") // No px - scales with viewBox
      .attr("font-weight", "500")
      .text("0°");

    // Note: Degree guide was drawn earlier, after orbits

    // Set up zoom behavior
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 10]) // Min zoom: 0.1x, Max zoom: 10x
      .on("zoom", (event) => {
        zoomGroup.attr("transform", event.transform);
      });

    // Apply zoom to the entire SVG
    svg.call(zoom);
    
    // Store zoom behavior reference for programmatic control
    zoomBehaviorRef.current = zoom;

  }, [planets, aspects, chartSize.width, chartSize.height]);

  // Zoom control functions
  const handleZoomIn = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    const centerX = BASE_SIZE / 2; // Use base size for zoom center
    const centerY = BASE_SIZE / 2;
    
    svg
      .transition()
      .duration(200)
      .call(
        zoomBehaviorRef.current.scaleBy,
        1.5,
        [centerX, centerY]
      );
  };

  const handleZoomOut = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    const centerX = BASE_SIZE / 2; // Use base size for zoom center
    const centerY = BASE_SIZE / 2;
    
    svg
      .transition()
      .duration(200)
      .call(
        zoomBehaviorRef.current.scaleBy,
        1 / 1.5,
        [centerX, centerY]
      );
  };

  const handleFitToView = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    
    svg
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  };

  return (
    <div className="d3-helio-chart-wrapper" ref={containerRef}>
      <div className="zoom-controls">
        <button
          className="zoom-btn zoom-in"
          onClick={handleZoomIn}
          title="Zoom In (or use mouse wheel)"
          aria-label="Zoom In"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 3V13M3 8H13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          className="zoom-btn zoom-out"
          onClick={handleZoomOut}
          title="Zoom Out (or use mouse wheel)"
          aria-label="Zoom Out"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8H13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          className="zoom-btn zoom-fit"
          onClick={handleFitToView}
          title="Fit to View (reset zoom and pan)"
          aria-label="Fit to View"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 2H6V6M10 2H14V6M14 10V14H10M6 14H2V10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="zoom-hint">
        <span>Scroll to zoom • Drag to pan</span>
      </div>
      <div className="chart-svg-container">
        <svg ref={svgRef} className="d3-helio-chart" />
      </div>
    </div>
  );
};

D3HelioChart.propTypes = {
  planets: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      longitude: PropTypes.number.isRequired,
      distanceAu: PropTypes.number,
      label: PropTypes.string,
    })
  ),
  aspects: PropTypes.arrayOf(
    PropTypes.shape({
      planets: PropTypes.arrayOf(PropTypes.string).isRequired,
      aspect: PropTypes.string.isRequired,
      type: PropTypes.string,
      orb: PropTypes.number,
    })
  ),
  width: PropTypes.number,
  height: PropTypes.number,
};

D3HelioChart.defaultProps = {
  planets: [],
  aspects: [],
  width: 800,
  height: 800,
};

export default D3HelioChart;
